const createPaginationState = () => ({
  pageMap: new Map([[1, null]]),
  currentPage: 1,
  highestKnownPage: 1,
  hasMorePages: false,
  lastEndCursor: null
});

const updatePaginationState = (state, pageNumber, hasNextPage, endCursor) => {
  if (pageNumber > state.highestKnownPage) {
    state.highestKnownPage = pageNumber;
  }

  state.hasMorePages = hasNextPage;

  if (hasNextPage) {
    state.pageMap.set(pageNumber + 1, endCursor);
  }

  if (pageNumber >= state.highestKnownPage) {
    state.lastEndCursor = endCursor;
  }
};

const getCursorForPage = (state, pageNumber) => {
  if (!state.pageMap.has(pageNumber)) {
    return undefined;
  }
  return state.pageMap.get(pageNumber);
};

const getWindowedPageRange = (currentPage, highestKnownPage, hasMorePages) => {
  const highestDisplayPage = hasMorePages ? highestKnownPage + 1 : highestKnownPage;

  if (highestDisplayPage <= 5) {
    return Array.from({ length: highestDisplayPage }, (_, i) => i + 1);
  }

  const windowStart = Math.max(currentPage - 1, 1);
  const windowEnd = Math.min(currentPage + 1, highestDisplayPage);

  const pages = new Set([1]);

  Array.from({ length: windowEnd - windowStart + 1 }, (_, i) => windowStart + i)
    .forEach((page) => pages.add(page));

  if (!hasMorePages) {
    pages.add(highestDisplayPage);
  }

  return Array.from(pages).sort((a, b) => a - b);
};

const createPageButton = (pageNumber, isActive, onClick) => {
  const button = document.createElement('button');
  button.classList.add('pagination-number');

  if (isActive) {
    button.classList.add('active');
    button.setAttribute('aria-current', 'page');
  }

  button.setAttribute('aria-label', `Page ${pageNumber}`);
  button.textContent = pageNumber;
  button.addEventListener('click', onClick);

  return button;
};

const createEllipsis = () => {
  const span = document.createElement('span');
  span.classList.add('pagination-ellipsis');
  span.setAttribute('aria-hidden', 'true');
  span.textContent = '\u2026';

  return span;
};

const appendEllipsisIfGap = (container, pageNumber, previousPage) => {
  if (previousPage === undefined) {
    return;
  }
  if (pageNumber - previousPage <= 1) {
    return;
  }
  container.appendChild(createEllipsis());
};

const renderPaginationNumbers = (state, onPageClick) => {
  const container = document.getElementById('pagination-numbers');
  if (!container) {
    return;
  }

  container.innerHTML = '';

  const pages = getWindowedPageRange(state.currentPage, state.highestKnownPage, state.hasMorePages);
  const highestDisplayPage = state.hasMorePages ? state.highestKnownPage + 1 : state.highestKnownPage;

  pages.forEach((pageNumber, index) => {
    appendEllipsisIfGap(container, pageNumber, pages[index - 1]);

    const isActive = pageNumber === state.currentPage;
    const button = createPageButton(pageNumber, isActive, () => onPageClick(pageNumber));
    container.appendChild(button);
  });

  if (state.hasMorePages && pages[pages.length - 1] === highestDisplayPage) {
    container.appendChild(createEllipsis());
  }
};

const updatePaginationVisibility = (currentPage, highestKnownPage, hasMorePages) => {
  const wrapper = document.querySelector('.pagination--wrapper');
  if (!wrapper) {
    return;
  }

  const isSinglePage = currentPage === 1 && !hasMorePages && highestKnownPage === 1;
  wrapper.classList.toggle('is-hidden', isSinglePage);
};

const buttonControllers = new Map();

const applyButtonState = (button, isEnabled, handler) => {
  if (!button) {
    return;
  }

  const buttonId = button.id;

  if (buttonControllers.has(buttonId)) {
    buttonControllers.get(buttonId).abort();
    buttonControllers.delete(buttonId);
  }

  if (!isEnabled) {
    button.classList.add('disabled');
    button.setAttribute('disabled', true);
    return;
  }

  button.classList.remove('disabled');
  button.removeAttribute('disabled');

  const controller = new AbortController();
  buttonControllers.set(buttonId, controller);
  button.addEventListener('click', handler, { signal: controller.signal });
};

const updatePaginationButtons = (hasPrevious, hasNext, onPrev, onNext) => {
  const paginationNumbers = document.getElementById('pagination-numbers');
  if (paginationNumbers) {
    paginationNumbers.innerHTML = '';
  }

  applyButtonState(document.getElementById('prev-button'), hasPrevious, onPrev);
  applyButtonState(document.getElementById('next-button'), hasNext, onNext);
};

const getPageFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  const page = parseInt(params.get('p'), 10);
  return page >= 1 ? page : 1;
};

const updateUrlPage = (pageNumber) => {
  const url = new URL(window.location.href);

  if (pageNumber <= 1) {
    url.searchParams.delete('p');
    window.history.replaceState(null, '', url);
    return;
  }

  url.searchParams.set('p', pageNumber);

  window.history.replaceState(null, '', url);
};

window.RecipePagination = {
  createPaginationState,
  updatePaginationState,
  getCursorForPage,
  renderPaginationNumbers,
  updatePaginationVisibility,
  updatePaginationButtons,
  getPageFromUrl,
  updateUrlPage
};
